import { useEffect, useState } from 'react'
import { toast } from 'sonner'
import { useCourseJob } from '@/features/ai/useCourseJob'
import { useCancelTranslationRun } from '@/features/ai/useCancelTranslationRun'
import { translationRunView, type TranslationRunView } from '@/features/ai/lib/translationRun'
import type { JobEntry } from '@/features/ai/jobsSlice'
import type { TranslationDialog } from './TranslationDialogs'
import { useRunFinishedToast } from './useRunFinishedToast'
import { useTranslationLauncher } from './useTranslationLauncher'

/**
 * Джобы, которые этот экземпляр редактора видел ИДУЩИМИ. Итог прогона
 * показывается только им: запись реестра лежит в persist бессрочно, и без
 * этого итог давно законченного прогона всплывал бы на каждом открытии
 * редактора. Выбрано наблюдение, а не сравнение `finished_at` со временем
 * монтирования: часы сервера и браузера расходятся, и на стыке сравнение
 * врало бы в обе стороны.
 */
const useSeenRunning = (view: TranslationRunView): ReadonlySet<number> => {
  const [seen, setSeen] = useState<ReadonlySet<number>>(() => new Set())
  const runningId = view.kind === 'running' ? view.jobId : null
  useEffect(() => {
    if (runningId === null) return
    setSeen((prev) => (prev.has(runningId) ? prev : new Set(prev).add(runningId)))
  }, [runningId])
  return seen
}

/** Итог принадлежит прогону, который видели идущим, — сам или его
 *  предшественник по цепочке «перевод → картинки» (§7.4). */
const wasSeen = (seen: ReadonlySet<number>, jobId: number, entry: JobEntry | null): boolean =>
  seen.has(jobId) || (entry?.previous?.kind === 'chain' && seen.has(entry.previous.jobId))

/**
 * Проводка перевода для меню языка шапки: вид прогона (опрос ведёт
 * `JobPoller`, здесь только чтение реестра), запуск, отмена и диалоги.
 *
 * Действие с диалогом сначала закрывает меню (`closeMenu`), потом открывает
 * диалог — сам диалог живёт рядом с меню, не внутри его содержимого.
 */
export const useMenuTranslation = (
  courseId: number,
  startBlocked: boolean,
  closeMenu: () => void,
  silentFinish = false,
) => {
  const { job, entry } = useCourseJob(courseId)
  const view = translationRunView(entry, job)
  useRunFinishedToast(view, silentFinish)
  const seen = useSeenRunning(view)
  const launcher = useTranslationLauncher(courseId, startBlocked)
  const canceller = useCancelTranslationRun(courseId)
  const [dialog, setDialog] = useState<TranslationDialog>(null)
  // Итог прячется локально: `detach` записи стёр бы и ленту чата в сайдбаре.
  // Здесь, а не в подвале меню: содержимое меню размонтируется при закрытии.
  const [dismissedJobId, setDismissedJobId] = useState<number | null>(null)
  const ask = (next: Exclude<TranslationDialog, null>) => {
    closeMenu()
    setDialog(next)
  }
  return {
    isRunning: view.kind === 'running',
    /** Итог показываем один раз: закрыли меню с законченным прогоном — больше не всплывает. */
    onMenuClosed: () => {
      if (view.kind === 'finished') setDismissedJobId(view.jobId)
    },
    section: {
      view,
      launcher,
      canceller,
      onAsk: ask,
      showSummary:
        view.kind === 'finished' &&
        view.jobId !== dismissedJobId &&
        wasSeen(seen, view.jobId, entry),
      onDismiss: setDismissedJobId,
    },
    dialogs: {
      open: dialog,
      onOpenChange: setDialog,
      launcher,
      onCancelRun: () => {
        setDialog(null)
        // Отмену подтверждают при закрытом меню: отказ, кроме строки в блоке
        // прогона, приходит тостом.
        void canceller.cancel().then((failure) => {
          if (failure) toast.error(failure)
        })
      },
    },
  }
}
